import { word } from "@modules/core/utils/i18n"
import { getFormErrorMessage } from "@modules/formik/formikUtils"
import { MediaItemFormikType } from "@modules/sessionBlock/media/mediaFormikTypes"
import { useSessionFormContext } from "@modules/sessions/hooks/useSessionFormContext"
import { useSessionSidebar } from "@modules/sessions/hooks/useSessionSidebar"
import { MediaUpload } from "@modules/ui/components/fileUpload/mediaUpload"
import { HUButton } from "@modules/ui/components/huButton"
import { HUText } from "@modules/ui/components/huText"
import { isYoutubeLink } from "@modules/utils/convertYoutubeLink"
import get from "lodash/get"
import { useTheme } from "styled-components"

type Props = {} & React.ComponentProps<"div">

export const MediaForm: React.FC<Props> = ({ ...props }) => {
  const theme = useTheme()
  const { sidebarNavigateBack } = useSessionSidebar()
  const { setInitialUpdatedBlock, formik, updatedBlockIndex } = useSessionFormContext()

  if (updatedBlockIndex === null || !formik) {
    return null
  }

  const formikNameBase = `blocks[${updatedBlockIndex}].content.medias`
  const formikValue = get(formik.values, formikNameBase) as MediaItemFormikType[]
  const isUploadingAMedia = formikValue && formikValue.some((media) => media.isUploading)

  const validateForm = async () => {
    await formik.setFieldTouched(formikNameBase)
    const errors = await formik.validateForm()

    const url = get(formik.values, `${formikNameBase}.url`) as string | undefined

    if (url && !isYoutubeLink(url)) {
      formik.setFieldError(`${formikNameBase}.url`, word("sessionForm.blocks.add_media.youtube_error"))
      return
    }

    if (!get(errors, formikNameBase)) {
      setInitialUpdatedBlock(undefined)
      sidebarNavigateBack(false)
    }
  }

  return (
    <div className="flex flex-column gap-5 flex-grow-1 session-block-form pb-3" {...props}>
      <div className="flex flex-column gap-2 flex-grow-1">
        {updatedBlockIndex !== null && (
          <HUText fontStyle="LS" color={theme.neutralColor700} className="uppercase">
            {`(${updatedBlockIndex + 1}) ${word("program.planning.create.exercise.block_plural", { count: updatedBlockIndex })}`}
          </HUText>
        )}

        <HUText fontStyle="TL">{word("sessionForm.blocks.add_media.title")}</HUText>
        <HUText fontStyle="BS">{word("sessionForm.blocks.add_media.description")}</HUText>
      </div>
      <div>
        <MediaUpload
          name="medias"
          mediaList={formikValue}
          updateMediaList={(mediaList) => {
            formik.setFieldValue(`${formikNameBase}`, mediaList)
          }}
          updateMediaItem={(mediaItem, index) => {
            formik.setFieldValue(`${formikNameBase}[${index}]`, mediaItem)
          }}
        />
      </div>

      <div className="flex flex-column align-items-start justify-content-between">
        {typeof get(formik.errors, formikNameBase) === "string" && getFormErrorMessage(formikNameBase, formik)}

        <HUButton
          type="Default"
          colorType="Primary"
          size="L"
          text={word("global.check")}
          className="p-button-primary w-full"
          onClick={validateForm}
          disabled={isUploadingAMedia}
        />
      </div>
    </div>
  )
}
